import { useRef } from 'react';
import { QrCode, ScanLine, Container as ContainerIcon, Route, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useReveal } from '@/lib/useReveal';
import { Section, Container, Display, Lead, CtaLink } from '@/components/system';

/**
 * The package's journey through MEC — Apple-style horizontal gallery.
 * Who does each handoff and which app records it. QR labels (what exists today);
 * no RFID; Mi Cargo is "en camino". MEC never touches the box.
 */
const stages = [
  { n: '01', icon: QrCode, title: 'Registro y etiqueta', who: 'La agencia', app: 'Mi Dashboard',
    text: 'Crea el paquete, lo cotiza con el arancel integrado y le imprime su etiqueta QR por artículo.' },
  { n: '02', icon: ScanLine, title: 'Entrada al almacén', who: 'Almacén o transitaria', app: 'Mi Admin',
    text: 'Escaneo masivo al recibir: el estado de cientos de artículos se actualiza de una sola vez.' },
  { n: '03', icon: ContainerIcon, title: 'Despacho y llegada', who: 'La transitaria', app: 'Mi Admin · Mi Cargo, en camino',
    text: 'Consolidación por destino y lectura en cada traspaso: salida de EE.\u00a0UU., aduana, almacén en Cuba.' },
  { n: '04', icon: Route, title: 'Reparto por ruta', who: 'El repartidor', app: 'Mi Admin',
    text: 'Entregas asignadas por ruta. Escanea y entrega aunque no haya señal; sincroniza al reconectar.' },
  { n: '05', icon: CheckCircle, title: 'Entrega confirmada', who: 'Repartidor → cliente', app: 'Mi Envío Cuba',
    text: 'Foto, firma del receptor y GPS — visibles al instante en la app del cliente y en tu panel.' },
];

export function PackageJourney() {
  const ref = useReveal<HTMLElement>();
  const railRef = useRef<HTMLDivElement>(null);
  const scrollBy = (dir: 1 | -1) => railRef.current?.scrollBy({ left: dir * 360, behavior: 'smooth' });

  return (
    <Section tone="white" ref={ref} className="overflow-hidden">
      <Container>
        <div className="max-w-3xl">
          <p data-reveal className="text-brand font-semibold text-base sm:text-lg mb-4">El viaje de un paquete</p>
          <Display data-reveal size="lg">De la agencia en EE.&nbsp;UU. a la puerta en Cuba.</Display>
          <Lead data-reveal className="mt-6">
            Quién hace cada traspaso, y qué app de MEC lo registra — una sola etiqueta, leída en cada paso.
          </Lead>
        </div>
      </Container>

      {/* rail */}
      <div data-reveal className="mt-12 sm:mt-14">
        <div ref={railRef} className="gallery flex gap-5 overflow-x-auto px-6 sm:px-8 lg:px-[max(2rem,calc((100vw_-_1120px)/2))] pb-4">
          {stages.map((s) => (
            <article key={s.n} className="w-[300px] sm:w-[340px] shrink-0 rounded-[28px] bg-bg-light p-8 flex flex-col min-h-[360px]">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
                  <s.icon className="w-6 h-6 text-brand" />
                </div>
                <span className="text-text-tertiary font-semibold tabular-nums">{s.n}</span>
              </div>
              <h3 className="mt-8 font-heading text-2xl font-semibold tracking-tight">{s.title}</h3>
              <p className="mt-1 text-text-tertiary font-medium">{s.who}</p>
              <p className="mt-4 text-text-secondary leading-relaxed">{s.text}</p>
              <span className="mt-auto pt-6 text-sm font-semibold text-brand">{s.app}</span>
            </article>
          ))}
          <div className="w-2 shrink-0" aria-hidden="true" />
        </div>
        <Container className="mt-6 flex items-center justify-between">
          <p className="text-text-secondary text-lg font-medium max-w-2xl">
            MEC nunca toca la caja. Toca la información de la caja — en cada traspaso.{' '}
            <CtaLink to="/plataforma/mi-cargo" className="text-lg">¿Eres transitaria? Conoce Mi Cargo</CtaLink>
          </p>
          <div className="hidden md:flex items-center gap-2">
            <button type="button" onClick={() => scrollBy(-1)} aria-label="Anterior" className="w-10 h-10 rounded-full bg-bg-light hover:bg-line flex items-center justify-center transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button type="button" onClick={() => scrollBy(1)} aria-label="Siguiente" className="w-10 h-10 rounded-full bg-bg-light hover:bg-line flex items-center justify-center transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </Container>
      </div>
    </Section>
  );
}
